import _ from 'lodash';
import { CircleCheckIcon, TrendingUp } from 'lucide-react';

import { KencoFlashQuoteMetadata } from 'lib/api/getQuickQuote';
import { titleCase } from 'utils/formatStrings';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import { QuoteCardType } from './QuoteCard';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';
import { Flex } from './layout';
import { Typography } from './typography';

type QuoteCardKencoProps = {
  carrier: QuoteCardType;
  isSelected: boolean;
  onClick: () => void;
};

export const QuoteCardKenco = ({
  carrier,
  isSelected,
  onClick,
}: QuoteCardKencoProps) => {
  return (
    <TooltipProvider>
      <Flex
        key={carrier?.type}
        direction='col'
        className={cn(
          'relative w-full border-2 border-brand-400 rounded-[4px] p-2 transition-transform',
          isSelected
            ? 'bg-brand-50'
            : 'cursor-pointer hover:scale-[1.01] bg-neutral-50'
        )}
        onClick={onClick}
      >
        <Flex justify='between' align='center' className='w-full mb-2'>
          <Flex direction='col'>
            <Flex align='center' gap='xs'>
              <Typography variant='body-sm' weight='semibold'>
                {carrier.title}
              </Typography>
            </Flex>
          </Flex>
          {carrier?.icon}
        </Flex>

        <KencoFlashDetailSection
          carrier={carrier}
          metadata={carrier?.kencoMetadata ?? {}}
        />

        {isSelected && (
          <CircleCheckIcon
            data-testid='quote-card-selected-check'
            className='absolute -top-3 -right-2.5 fill-neutral-50 stroke-success-500! w-6 h-6'
            aria-hidden
          />
        )}
      </Flex>
    </TooltipProvider>
  );
};

function KencoFlashDetailSection({
  carrier,
  metadata,
}: {
  carrier: QuoteCardType;
  metadata: Partial<KencoFlashQuoteMetadata>;
}) {
  const popoverRootContainer = getDrumkitContainer();

  const {
    current_capacity_condition: currentCapacityCondition = '',
    directional_capacity_change: directionalCapacityChange = '',
    outbound_tender_rejection_percent: outboundTenderRejectionPercent,
    average_margin_pct: averageMarginPct = 0,
  } = metadata;

  const {
    cost,
    costPerMile,
    priceRange,
    priceRangePerMile,
    inputtedTransportType,
    actualTransportType,
    quotingIntegrationName,
  } = carrier;

  const roundedCostPerMile =
    costPerMile != null ? _.round(costPerMile, 2) : null;

  const roundedPriceRange = priceRange
    ? {
        lowEstimate: _.round(priceRange.lowEstimate),
        highEstimate: _.round(priceRange.highEstimate),
      }
    : null;

  const roundedPriceRangePerMile = priceRangePerMile
    ? {
        lowEstimate: _.round(priceRangePerMile.lowEstimate, 2),
        highEstimate: _.round(priceRangePerMile.highEstimate, 2),
      }
    : null;

  const shouldShowCapacityCoverage =
    currentCapacityCondition ||
    directionalCapacityChange ||
    outboundTenderRejectionPercent != null;

  const currentCapacityConditionColorCoding = kencoCapacityCoverageColorCoding(
    currentCapacityCondition ?? ''
  );

  return (
    <Flex direction='col' className='w-full gap-0'>
      <Flex justify='between' align='center' className='w-full mt-2'>
        <Typography className='shrink-0 text-[13px]'>Equipment Type</Typography>
        {inputtedTransportType !== actualTransportType ? (
          <Flex align='baseline' gap='sm'>
            <Tooltip delayDuration={10}>
              <TooltipTrigger className='border-b border-dashed border-neutral-900'>
                <Typography variant='body-xs'>
                  {titleCase(actualTransportType)}
                </Typography>
              </TooltipTrigger>
              <TooltipPortal container={popoverRootContainer}>
                <TooltipContent className='mr-1 max-w-60 whitespace-pre-wrap'>
                  <Typography variant='body-xs'>
                    {`${quotingIntegrationName ?? 'Quoting integration'} `}
                    does not support {titleCase(inputtedTransportType)} quotes,
                    showing equivalent {titleCase(actualTransportType)} quote.
                  </Typography>
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </Flex>
        ) : (
          <Typography variant='body-xs'>
            {titleCase(actualTransportType)}
          </Typography>
        )}
      </Flex>

      <Flex justify='between' align='center' className='w-full mt-1'>
        <Flex align='center' gap='sm'>
          <Typography className='shrink-0 text-[13px]'>Buy Rate</Typography>
        </Flex>
        <Flex align='baseline' className='gap-x-2 flex-wrap justify-end'>
          <Typography variant='body-xs'>{`$${_.round(cost).toLocaleString()}`}</Typography>
          {roundedCostPerMile != null && (
            <Typography variant='body-xs'>{`($${roundedCostPerMile}/mi)`}</Typography>
          )}
        </Flex>
      </Flex>

      {roundedPriceRange && (
        <Flex justify='between' align='center' className='w-full mt-1'>
          <Flex align='center' gap='sm'>
            <Typography className='shrink-0 text-[13px]'>
              Price Range
            </Typography>
          </Flex>
          {roundedPriceRangePerMile ? (
            <Tooltip delayDuration={10}>
              <TooltipTrigger className='border-b border-dashed border-neutral-900'>
                <Typography variant='body-xs'>
                  {`$${roundedPriceRange.lowEstimate.toLocaleString()} - $${roundedPriceRange.highEstimate.toLocaleString()}`}
                </Typography>
              </TooltipTrigger>
              <TooltipPortal container={popoverRootContainer}>
                <TooltipContent className='mr-2 max-w-60 whitespace-pre-wrap'>
                  <div className='pb-3'>
                    <Typography variant='body-xs'>
                      Price range per mile
                    </Typography>
                    <Typography variant='body-xs'>
                      {`$${roundedPriceRangePerMile.lowEstimate} - $${roundedPriceRangePerMile.highEstimate}/mi.`}
                    </Typography>
                  </div>
                  <Typography variant='body-sm' className='border-t pt-3'>
                    All prices include fuel.
                  </Typography>
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          ) : (
            <Typography variant='body-xs'>
              {`$${roundedPriceRange.lowEstimate.toLocaleString()} - $${roundedPriceRange.highEstimate.toLocaleString()}`}
            </Typography>
          )}
        </Flex>
      )}

      {averageMarginPct != null && (
        <Flex justify='between' align='center' className='w-full mt-1'>
          <Flex align='center' gap='sm'>
            <Typography className='shrink-0 text-[13px]'>Margin</Typography>
          </Flex>
          <Flex align='center' gap='sm'>
            <TrendingUp className='h-4 w-4 shrink-0 stroke-success-600!' />
            <Typography variant='body-xs' className='text-success-600'>
              {`${_.round(averageMarginPct, 2)}%`}
            </Typography>
          </Flex>
        </Flex>
      )}

      {shouldShowCapacityCoverage && (
        <>
          <div className='w-full border-t border-neutral-200 my-3' />
          <Typography variant='body-sm' weight='medium' className='w-full mb-1'>
            Capacity & Coverage
          </Typography>
        </>
      )}

      {outboundTenderRejectionPercent != null &&
        currentCapacityCondition != null && (
          <Flex justify='between' align='center' className='w-full mt-1 gap-2'>
            <Typography className='shrink-0 text-[13px]'>
              Outbound Tender Rejection
            </Typography>
            <Typography
              variant='body-xs'
              weight='semibold'
              className={cn('text-right', currentCapacityConditionColorCoding)}
            >
              {`${_.round(outboundTenderRejectionPercent, 2)}%`}
            </Typography>
          </Flex>
        )}
      {currentCapacityCondition && (
        <Flex justify='between' align='center' className='w-full mt-1 gap-2'>
          <Typography className='shrink-0 text-[13px]'>
            Current Capacity
          </Typography>
          <Typography
            variant='body-xs'
            weight='semibold'
            className={cn('text-right', currentCapacityConditionColorCoding)}
          >
            {currentCapacityCondition}
          </Typography>
        </Flex>
      )}
      {directionalCapacityChange && (
        <Flex justify='between' align='center' className='w-full mt-1 gap-2'>
          <Typography className='shrink-0 text-[13px]'>
            Directional Change
          </Typography>
          <Typography
            variant='body-xs'
            weight='semibold'
            className={cn(
              'text-right',
              kencoCapacityCoverageColorCoding(directionalCapacityChange)
            )}
          >
            {directionalCapacityChange}
          </Typography>
        </Flex>
      )}
    </Flex>
  );
}

// Tightening / Stable / Loosening → error / warning / success/
function kencoCapacityCoverageColorCoding(value: string): string {
  const t = value.trim();
  if (!t) {
    return 'text-neutral-900';
  }

  const isTightRegex =
    /\b(tighten(ing|ed|s|e|er|ing)?|tightening|tightened|tightens|tight)\b/i;
  const isBalancedRegex =
    /\b(balanc\w*|moderate|stable|minimal|average|neutral|fair)\b/i;
  const isLooseRegex =
    /\b(loose|looses|loosens?|loosening|loosened|looser|loosed|loosing)\b/i;

  if (isLooseRegex.test(t) && !isTightRegex.test(t)) {
    return 'text-success-600';
  }
  if (isBalancedRegex.test(t)) {
    return 'text-orange-400';
  }
  if (isTightRegex.test(t)) {
    return 'text-error-500';
  }
  return 'text-neutral-900';
}
